(function () {
  /** Magnification factor (higher = tighter crop in the side panel + smaller lens on the main image). */
  const ZOOM_SCALE = 2.5;
  /** Hover flyout + lens only at this breakpoint and up (matches theme CSS). */
  const ZOOM_DISABLED_MQ = '(max-width: 989px)';
  const cleanups = new Map();

  function teardownById(sectionId) {
    const off = cleanups.get(String(sectionId));
    if (off) {
      off();
      cleanups.delete(String(sectionId));
    }
  }

  function setupProductInfo(productInfo) {
    const sectionId = productInfo.dataset.section || productInfo.getAttribute('data-section');
    const wrap = productInfo.querySelector('[data-hover-zoom-wrap]');
    const panel = productInfo.querySelector('.product__hover-zoom-panel');

    if (!sectionId) {
      return () => {};
    }
    if (!wrap) {
      return () => {};
    }
    if (!panel) {
      return () => {};
    }

    /** Last pointer position + opener while zoom is open (used to refresh on scroll/resize). */
    let lastOpener = null;
    let lastClientX = 0;
    let lastClientY = 0;
    let scrollRaf = 0;

    function visualViewportBox() {
      const vv = window.visualViewport;
      if (!vv) {
        return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
      }
      return {
        top: vv.offsetTop,
        left: vv.offsetLeft,
        width: vv.width,
        height: vv.height,
      };
    }

    /** Intersection of product info column with the visual viewport (what is actually on screen). */
    function infoWrapVisibleRect(infoWrap) {
      const r = infoWrap.getBoundingClientRect();
      const v = visualViewportBox();
      const vRight = v.left + v.width;
      const vBottom = v.top + v.height;
      const left = Math.max(r.left, v.left);
      const top = Math.max(r.top, v.top);
      const right = Math.min(r.right, vRight);
      const bottom = Math.min(r.bottom, vBottom);
      return {
        left,
        top,
        width: Math.max(0, right - left),
        height: Math.max(0, bottom - top),
      };
    }

    function schedulePanelSync() {
      if (panel.hidden || !lastOpener) return;
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => {
        scrollRaf = 0;
        if (!panel.hidden && lastOpener && lastOpener.isConnected) {
          showAndUpdate(lastOpener, lastClientX, lastClientY);
        }
      });
    }

    function onScrollOrViewport() {
      schedulePanelSync();
    }

    const lens = document.createElement('div');
    lens.className = 'product__hover-zoom-lens';
    lens.hidden = true;
    lens.setAttribute('aria-hidden', 'true');
    wrap.appendChild(lens);

    function hideLens() {
      lens.hidden = true;
      lens.setAttribute('aria-hidden', 'true');
      lens.style.left = '';
      lens.style.top = '';
      lens.style.width = '';
      lens.style.height = '';
    }

    function hidePanel() {
      lastOpener = null;
      lastClientX = 0;
      lastClientY = 0;
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
      panel.style.position = '';
      panel.style.left = '';
      panel.style.top = '';
      panel.style.right = '';
      panel.style.bottom = '';
      panel.style.width = '';
      panel.style.height = '';
      panel.style.backgroundImage = '';
      panel.style.backgroundSize = '';
      panel.style.backgroundPosition = '';
      panel.style.backgroundRepeat = '';
      hideLens();
    }

    function showAndUpdate(opener, clientX, clientY) {
      const img = opener.querySelector('img.image-magnify-hover_panel');
      if (!img) {
        hidePanel();
        return;
      }
      let src = img.getAttribute('data-zoom-src');
      if (!src) src = img.currentSrc || img.src;
      if (!src) {
        hidePanel();
        return;
      }
      lastOpener = opener;
      lastClientX = clientX;
      lastClientY = clientY;

      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      panel.style.backgroundImage = 'url(' + JSON.stringify(src) + ')';
      panel.style.backgroundRepeat = 'no-repeat';

      const imgRect = img.getBoundingClientRect();
      if (imgRect.width < 1 || imgRect.height < 1) {
        hidePanel();
        return;
      }

      const infoWrap = panel.closest('.product__info-wrapper--hover-zoom');
      if (!infoWrap) {
        hidePanel();
        return;
      }

      const box = infoWrapVisibleRect(infoWrap);
      if (box.width < 8 || box.height < 8) {
        hidePanel();
        return;
      }

      panel.style.position = 'fixed';
      panel.style.left = `${Math.round(box.left)}px`;
      panel.style.top = `${Math.round(box.top)}px`;
      panel.style.right = 'auto';
      panel.style.bottom = 'auto';
      panel.style.width = `${Math.round(box.width)}px`;
      panel.style.height = `${Math.round(Math.max(200, box.height))}px`;

      const pw = Math.max(1, box.width);
      const ph = Math.max(1, Math.max(200, box.height));

      // Normalized pointer over the *visible* image (handles object-fit letterboxing).
      let mx = (clientX - imgRect.left) / imgRect.width;
      let my = (clientY - imgRect.top) / imgRect.height;
      mx = Math.min(1, Math.max(0, mx));
      my = Math.min(1, Math.max(0, my));

      // Size the zoom layer from the on-screen image footprint so aspect matches the thumbnail.
      const bgW = imgRect.width * ZOOM_SCALE;
      const bgH = imgRect.height * ZOOM_SCALE;
      panel.style.backgroundSize = `${bgW}px ${bgH}px`;

      // Percent background-position is relative to (panel − background), not the gallery opener.
      // Use pixel offsets so the focal point tracks the cursor on the preview panel.
      const posX = mx * (pw - bgW);
      const posY = my * (ph - bgH);
      panel.style.backgroundPosition = `${posX}px ${posY}px`;

      // Amazon-style “window” on the main image: shows which region maps to the flyout panel.
      const rawLensW = Math.min(imgRect.width, pw / ZOOM_SCALE);
      const rawLensH = Math.min(imgRect.height, ph / ZOOM_SCALE);
      const minLens = 36;
      const lw = Math.min(imgRect.width, Math.max(minLens, rawLensW));
      const lh = Math.min(imgRect.height, Math.max(minLens, rawLensH));
      let lensLeft = clientX - imgRect.left - lw / 2;
      let lensTop = clientY - imgRect.top - lh / 2;
      lensLeft = Math.max(0, Math.min(lensLeft, imgRect.width - lw));
      lensTop = Math.max(0, Math.min(lensTop, imgRect.height - lh));

      lens.hidden = false;
      lens.setAttribute('aria-hidden', 'false');
      const wrapRect = wrap.getBoundingClientRect();
      lens.style.left = `${Math.round(imgRect.left - wrapRect.left + lensLeft)}px`;
      lens.style.top = `${Math.round(imgRect.top - wrapRect.top + lensTop)}px`;
      lens.style.width = `${Math.round(lw)}px`;
      lens.style.height = `${Math.round(lh)}px`;
    }

    function resolveOpenerFromEvent(e) {
      const t = e.target;
      if (!t || !t.closest) return null;
      let opener = t.closest('.product__modal-opener--image');
      if (opener && opener.querySelector('img.image-magnify-hover_panel')) return opener;
      if (t === panel || panel.contains(t)) return null;
      const activeSlide = wrap.querySelector('.product__media-item.is-active');
      if (!activeSlide || !activeSlide.contains(t)) return null;
      const activeOpener = activeSlide.querySelector('.product__modal-opener--image');
      if (activeOpener && activeOpener.querySelector('img.image-magnify-hover_panel')) return activeOpener;
      return null;
    }

    function handlePointer(e) {
      if (window.matchMedia(ZOOM_DISABLED_MQ).matches) {
        hidePanel();
        return;
      }
      const t = e.target;
      if (!t) return;
      const overGallery = wrap.contains(t);
      const overPanel = t === panel || panel.contains(t);
      if (!overGallery && !overPanel) {
        return;
      }
      if (overPanel) {
        return;
      }
      const opener = resolveOpenerFromEvent(e);
      if (opener) {
        showAndUpdate(opener, e.clientX, e.clientY);
      } else {
        hidePanel();
      }
    }

    function onWrapPointerLeave(e) {
      const rt = e.relatedTarget;
      if (rt && (wrap.contains(rt) || (panel && (rt === panel || panel.contains(rt))))) return;
      hidePanel();
    }

    function onPanelPointerLeave(e) {
      const rt = e.relatedTarget;
      if (rt && (wrap.contains(rt) || panel.contains(rt))) return;
      hidePanel();
    }

    function onVariantChange(ev) {
      if (!ev?.data || String(ev.data.sectionId) !== String(sectionId)) return;
      hidePanel();
    }

    const cap = true;
    productInfo.addEventListener('pointermove', handlePointer, cap);
    productInfo.addEventListener('pointerover', handlePointer, cap);
    productInfo.addEventListener('mousemove', handlePointer, cap);
    wrap.addEventListener('pointerleave', onWrapPointerLeave);
    panel.addEventListener('pointerleave', onPanelPointerLeave);

    window.addEventListener('scroll', onScrollOrViewport, { passive: true, capture: true });
    document.addEventListener('scroll', onScrollOrViewport, { passive: true, capture: true });
    window.addEventListener('resize', onScrollOrViewport);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onScrollOrViewport);
      window.visualViewport.addEventListener('scroll', onScrollOrViewport);
    }

    const infoColumn = panel.closest('.product__info-wrapper--hover-zoom');
    let intersectionObserver;
    if (infoColumn && typeof IntersectionObserver !== 'undefined') {
      intersectionObserver = new IntersectionObserver(
        () => {
          schedulePanelSync();
        },
        { root: null, rootMargin: '0px', threshold: [0, 0.05, 0.15, 0.35, 0.55, 0.75, 1] }
      );
      intersectionObserver.observe(infoColumn);
    }

    let unsub;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      unsub = subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
    }

    return () => {
      window.removeEventListener('scroll', onScrollOrViewport, { capture: true });
      document.removeEventListener('scroll', onScrollOrViewport, { capture: true });
      window.removeEventListener('resize', onScrollOrViewport);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onScrollOrViewport);
        window.visualViewport.removeEventListener('scroll', onScrollOrViewport);
      }
      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }
      if (scrollRaf) {
        cancelAnimationFrame(scrollRaf);
        scrollRaf = 0;
      }
      productInfo.removeEventListener('pointermove', handlePointer, cap);
      productInfo.removeEventListener('pointerover', handlePointer, cap);
      productInfo.removeEventListener('mousemove', handlePointer, cap);
      wrap.removeEventListener('pointerleave', onWrapPointerLeave);
      panel.removeEventListener('pointerleave', onPanelPointerLeave);
      if (typeof unsub === 'function') unsub();
      hidePanel();
      lens.remove();
    };
  }

  function mountAll() {
    const roots = document.querySelectorAll('product-info[data-image-zoom="hover_panel"]');
    if (roots.length === 0) {
      return;
    }
    roots.forEach((productInfo) => {
      const sectionId = productInfo.dataset.section || productInfo.getAttribute('data-section');
      if (!sectionId) {
        return;
      }
      teardownById(sectionId);
      cleanups.set(String(sectionId), setupProductInfo(productInfo));
    });
  }

  function onSectionUnload(ev) {
    const root = ev.target;
    if (!root?.querySelectorAll) return;
    root.querySelectorAll('product-info[data-image-zoom="hover_panel"]').forEach((pi) => {
      const sid = pi.dataset.section || pi.getAttribute('data-section');
      if (sid) teardownById(sid);
    });
  }

  function bindMountHooks() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', mountAll, { once: true });
    } else {
      mountAll();
    }
    document.addEventListener('shopify:section:load', mountAll);
    document.addEventListener('shopify:section:unload', onSectionUnload);
    document.addEventListener(
      'product-info:loaded',
      (ev) => {
        const el = ev.target;
        if (el && el.matches && el.matches('product-info[data-image-zoom="hover_panel"]')) {
          mountAll();
        }
      },
      true
    );
  }

  if (!window.__pdpHoverZoomInit) {
    window.__pdpHoverZoomInit = true;
    bindMountHooks();
  } else {
    mountAll();
  }
})();
